'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { fetchCatalog, useSession, type CatalogTenant } from '../lib/auth';
import { oidcConfig } from '../lib/config';

/**
 * OAuth2 gate boundary (§6c). Renders a sign-in screen until a session exists,
 * then the app with a header showing the tenant and a sign-out. This is the
 * deterministic-first seam for the real OIDC flow — see `lib/auth.ts`.
 */
export function AuthGate({ children }: { children: ReactNode }): React.JSX.Element {
  const { session, ready, error, signIn, signOut } = useSession();
  const oidc = oidcConfig() !== null;
  const [catalog, setCatalog] = useState<CatalogTenant[]>([]);
  const [tenant, setTenant] = useState('');

  // Dev only: load the tenants that have a model so a newly-onboarded client
  // appears automatically in the picker. The bundled `demo` tenant is excluded —
  // it's dev scaffolding, not a real client.
  useEffect(() => {
    if (oidc) return;
    void fetchCatalog().then((rows) => {
      const tenants = rows.filter((r) => r.tenantId !== 'demo');
      setCatalog(tenants);
      if (tenants[0]) setTenant(tenants[0].tenantId);
    });
  }, [oidc]);

  if (!ready) return <div style={{ padding: 40 }} className="muted">Loading…</div>;

  if (!session) {
    return (
      <main style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 24 }}>
        <div className="card" style={{ maxWidth: 380, textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 8px', fontSize: 22 }}>dgt-ai-reporting</h1>
          <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>
            Governed AI reporting. Sign in to continue.
          </p>
          {!oidc && (
            <label style={{ display: 'block', textAlign: 'left', marginTop: 14, fontSize: 12 }} className="muted">
              Tenant
              <select
                value={tenant}
                onChange={(e) => setTenant(e.target.value)}
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: 5,
                  padding: '9px 10px',
                  fontSize: 14,
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--surface-1)',
                  color: 'var(--text)',
                }}
              >
                {catalog.length === 0 && (
                  <option value="" disabled>
                    No tenants yet — onboard a connector
                  </option>
                )}
                {catalog.map((c) => (
                  <option key={c.tenantId} value={c.tenantId}>
                    {c.tenantId} · {c.modelCount} model{c.modelCount === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            disabled={!oidc && !tenant}
            onClick={() => signIn(oidc ? undefined : tenant)}
            style={{
              marginTop: 12,
              width: '100%',
              padding: '10px 16px',
              fontSize: 15,
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              background: 'var(--series-1)',
              color: '#fff',
              cursor: !oidc && !tenant ? 'not-allowed' : 'pointer',
              opacity: !oidc && !tenant ? 0.5 : 1,
            }}
          >
            {oidc ? 'Continue with SSO' : tenant ? `Continue as ${tenant}` : 'No tenant available'}
          </button>
          {error ? (
            <p style={{ fontSize: 12, color: 'var(--series-1)', margin: '10px 0 0' }}>{error}</p>
          ) : null}
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            Production uses OAuth2 / OIDC with tenant-claim mapping.
          </p>
        </div>
      </main>
    );
  }

  return (
    <>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface-1)',
        }}
      >
        <strong>dgt-ai-reporting</strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13 }}>
          <span className="muted">
            {session.displayName} · tenant <code>{session.tenantId}</code>
          </span>
          <button
            type="button"
            onClick={signOut}
            style={{
              padding: '5px 12px',
              fontSize: 13,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      {children}
    </>
  );
}
